// src/components/Income/IncomeDetailModal.js
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getFiscalConfig } from '../../utils/fiscalConfig';
import './IncomeDetailModal.css';

/**
 * Configuration des champs par type de revenu
 * fieldType: 'auto' | 'estimated' | 'manual'
 * - auto: Calculé automatiquement (fiable)
 * - estimated: Suggestion basée sur données moyennes
 * - manual: L'utilisateur doit saisir
 */
const INCOME_FIELDS_CONFIG = {
  SALARIE: {
    label: 'Salarié - Détail des charges',
    icon: '💼',
    sections: [
      { id: 'charges', label: 'Charges sociales' },
      { id: 'total', label: '', isTotal: true },
      { id: 'abattement', label: 'Calcul du revenu imposable' }
    ],
    fields: [
      {
        key: 'employeeContributions',
        label: 'Cotisations salariales',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'Brut × 23%',
        calculate: (data, year) => Math.round((data.grossAnnual || 0) * 0.23)
      },
      {
        key: 'mutuelle',
        label: 'Mutuelle obligatoire',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€/an',
        section: 'charges',
        formula: 'Moyenne marché: 50€/mois',
        defaultValue: 600
      },
      {
        key: 'ticketsRestaurant',
        label: 'Tickets restaurant',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€/an',
        section: 'charges',
        formula: '4€/jour × 220 jours',
        defaultValue: 880
      },
      {
        key: 'transportPass',
        label: 'Transport',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€/an',
        section: 'charges',
        formula: 'Navigo 950€ × 50%',
        defaultValue: 475
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        formula: 'Cotisations + Mutuelle + Tickets',
        calculate: (data) => Math.round(
          (data.employeeContributions || 0) +
          (data.mutuelle || 0) +
          (data.ticketsRestaurant || 0)
        )
      },
      {
        key: 'deductionType',
        label: 'Type d\'abattement',
        type: 'select',
        fieldType: 'manual',
        section: 'abattement',
        options: [
          { value: 'forfait', label: 'Forfait 10% (défaut)' },
          { value: 'fraisReels', label: 'Frais réels' }
        ],
        defaultValue: 'forfait',
        formula: 'Forfait: min 495€, max 14171€'
      },
      {
        key: 'fraisReels',
        label: 'Frais réels déclarés',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'abattement',
        formula: 'Km, repas, formation...',
        showIf: (data) => data.deductionType === 'fraisReels'
      }
    ]
  },

  REMUNERATION_DIRIGEANT: {
    label: 'Dirigeant de société - Abattement fiscal',
    icon: '👔',
    sections: [
      { id: 'recap', label: 'Récapitulatif (depuis l\'entité)' },
      { id: 'abattement', label: 'Calcul du revenu imposable' }
    ],
    fields: [
      {
        key: 'grossAnnual',
        label: 'Brut annuel (entité)',
        type: 'number',
        fieldType: 'readonly',
        suffix: '€',
        section: 'recap',
        formula: 'Calculé depuis l\'entité liée'
      },
      {
        key: 'chargesSalariales',
        label: 'Charges salariales (~22%)',
        type: 'number',
        fieldType: 'readonly',
        suffix: '€',
        section: 'recap',
        formula: 'AS: 22% du brut, TNS: 45% du brut'
      },
      {
        key: 'netPercu',
        label: 'Net perçu',
        type: 'number',
        fieldType: 'readonly',
        suffix: '€',
        section: 'recap',
        formula: 'Brut - Charges salariales'
      },
      {
        key: 'abatementType',
        label: 'Type d\'abattement',
        type: 'select',
        fieldType: 'manual',
        section: 'abattement',
        options: [
          { value: 'percent10', label: 'Forfait 10% (défaut)' },
          { value: 'fraisReels', label: 'Frais réels' }
        ],
        defaultValue: 'percent10',
        formula: 'Forfait: min 495€, max 14 171€'
      },
      {
        key: 'fraisReels',
        label: 'Frais réels déclarés',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'abattement',
        formula: 'Km domicile-travail, repas, formation...',
        showIf: (data) => data.abatementType === 'fraisReels'
      },
      {
        key: 'abatementAmount',
        label: 'Montant de l\'abattement',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'abattement',
        calculate: (data, year) => {
          const net = data.netPercu || 0;
          if (data.abatementType === 'fraisReels') {
            return data.fraisReels || 0;
          }
          // Forfait 10% avec min 495€ et max 14171€
          return Math.min(Math.max(Math.round(net * 0.10), 495), 14171);
        }
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'abattement',
        isTotal: true,
        formula: 'Net perçu - Abattement',
        calculate: (data) => {
          const net = data.netPercu || 0;
          const abatement = data.abatementAmount || 0;
          return Math.max(0, net - abatement);
        }
      }
    ]
  },

  CHOMAGE: {
    label: 'Chômage (ARE)',
    icon: '📋',
    sections: [
      { id: 'charges', label: 'Prélèvements sociaux' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      // Section charges
      {
        key: 'grossAnnual',
        section: 'charges',
        label: 'Allocation brute annuelle',
        type: 'number',
        fieldType: 'readonly',
        suffix: '€',
        help: 'Montant saisi dans le wizard'
      },
      {
        key: 'csgRate',
        section: 'charges',
        label: 'Taux CSG/CRDS applicable',
        type: 'select',
        fieldType: 'manual',
        options: [
          { value: 'exonere', label: 'Exonéré (allocation < seuil)' },
          { value: 'reduit', label: 'Taux réduit 3.8%' },
          { value: 'normal', label: 'Taux normal 6.7%' }
        ],
        defaultValue: 'normal',
        help: 'Dépend du montant de l\'allocation journalière'
      },
      {
        key: 'csgCrds',
        section: 'charges',
        label: 'CSG/CRDS prélevée',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        calculate: (data) => {
          const brut = data.grossAnnual || 0;
          const rates = { exonere: 0, reduit: 0.038, normal: 0.067 };
          const rate = rates[data.csgRate] || 0.067;
          return Math.round(brut * rate);
        }
      },
      // Section total
      {
        key: 'totalCharges',
        section: 'total',
        label: 'Total prélèvements',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        isTotal: true,
        calculate: (data) => data.csgCrds || 0
      },
      {
        key: 'netBeforeTax',
        section: 'total',
        label: 'Net avant impôt',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        calculate: (data) => (data.grossAnnual || 0) - (data.csgCrds || 0)
      },
      // Section fiscal
      {
        key: 'taxableIncome',
        section: 'fiscal',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        formula: 'Net × 90% (abattement 10%)',
        suffix: '€',
        help: 'Même abattement 10% que les salaires',
        calculate: (data, year) => {
          const config = getFiscalConfig(year);
          const net = data.netBeforeTax || 0;
          const deduction = Math.min(
            Math.max(net * config.salaryDeduction.rate, config.salaryDeduction.min),
            config.salaryDeduction.max
          );
          return Math.round(net - deduction);
        }
      }
    ]
  },

  RETRAITE: {
    label: 'Retraite',
    icon: '👴',
    fields: [
      {
        key: 'grossPension',
        label: 'Pension brute annuelle',
        type: 'number',
        fieldType: 'manual',
        help: 'Total des pensions de retraite (base + complémentaire)',
        suffix: '€'
      },
      {
        key: 'csgRate',
        label: 'Taux CSG applicable',
        type: 'select',
        fieldType: 'auto',
        options: [
          { value: 'exonere', label: 'Exonéré (RFR < seuil)' },
          { value: 'reduit', label: 'Taux réduit 3.8%' },
          { value: 'median', label: 'Taux médian 6.6%' },
          { value: 'normal', label: 'Taux normal 8.3%' }
        ],
        defaultValue: 'normal',
        help: 'Dépend du RFR N-2. Normal: 8.3% CSG + 0.5% CRDS + 0.3% CASA'
      },
      {
        key: 'socialContributions',
        label: 'Prélèvements sociaux',
        type: 'number',
        fieldType: 'auto',
        help: 'CSG + CRDS + CASA selon taux applicable',
        suffix: '€',
        calculate: (data) => {
          const rates = { exonere: 0, reduit: 0.043, median: 0.074, normal: 0.091 };
          return Math.round((data.grossPension || 0) * (rates[data.csgRate] || 0.091));
        }
      },
      {
        key: 'netBeforeTax',
        label: 'Net avant impôt',
        type: 'number',
        fieldType: 'auto',
        help: 'Pension nette = Brute - Prélèvements sociaux',
        suffix: '€',
        calculate: (data) => Math.round((data.grossPension || 0) - (data.socialContributions || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        help: 'Après abattement 10% spécifique pensions (plafond différent des salaires)',
        suffix: '€',
        calculate: (data, year) => {
          const config = getFiscalConfig(year);
          const net = data.netBeforeTax || 0;
          const deduction = Math.min(
            Math.max(net * config.pensionDeduction.rate, config.pensionDeduction.min),
            config.pensionDeduction.max
          );
          return Math.round(net - deduction);
        }
      }
    ]
  },

  // EI Micro-BNC (Profession libérale)
  EI_MICRO_BNC: {
    label: 'EI Micro-BNC - Charges',
    icon: '🏪',
    sections: [
      { id: 'charges', label: 'Charges sociales' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' },
      { id: 'options', label: 'Options' }
    ],
    fields: [
      {
        key: 'urssafContributions',
        label: 'Cotisations URSSAF',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 21.1%',
        calculate: (data) => {
          const rate = data.acre ? 0.1055 : 0.211;
          return Math.round((data.grossAnnual || 0) * rate);
        }
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon CA et commune',
        defaultValue: 500
      },
      {
        key: 'cfp',
        label: 'Formation (CFP)',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 0.2%',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.002)
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.urssafContributions || 0) + (data.cfe || 0) + (data.cfp || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA × 66% (abattement 34%)',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.66)
      },
      {
        key: 'acre',
        label: 'ACRE 1ère année',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: 'Réduit URSSAF de 50%',
        defaultValue: false
      },
      {
        key: 'versementLiberatoire',
        label: 'Versement libératoire IR',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: '+2.2% du CA (IR inclus)',
        defaultValue: false
      }
    ]
  },

  // EI Micro-BIC Services
  EI_MICRO_BIC_SERVICE: {
    label: 'EI Micro-BIC Services - Charges',
    icon: '🏪',
    sections: [
      { id: 'charges', label: 'Charges sociales' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' },
      { id: 'options', label: 'Options' }
    ],
    fields: [
      {
        key: 'urssafContributions',
        label: 'Cotisations URSSAF',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 21.2%',
        calculate: (data) => {
          const rate = data.acre ? 0.106 : 0.212;
          return Math.round((data.grossAnnual || 0) * rate);
        }
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon CA et commune',
        defaultValue: 500
      },
      {
        key: 'cfp',
        label: 'Formation (CFP)',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 0.3%',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.003)
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.urssafContributions || 0) + (data.cfe || 0) + (data.cfp || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA × 50% (abattement 50%)',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.50)
      },
      {
        key: 'acre',
        label: 'ACRE 1ère année',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: 'Réduit URSSAF de 50%',
        defaultValue: false
      },
      {
        key: 'versementLiberatoire',
        label: 'Versement libératoire IR',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: '+1.7% du CA (IR inclus)',
        defaultValue: false
      }
    ]
  },

  // EI Micro-BIC Vente
  EI_MICRO_BIC_VENTE: {
    label: 'EI Micro-BIC Vente - Charges',
    icon: '🏪',
    sections: [
      { id: 'charges', label: 'Charges sociales' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' },
      { id: 'options', label: 'Options' }
    ],
    fields: [
      {
        key: 'urssafContributions',
        label: 'Cotisations URSSAF',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 12.3%',
        calculate: (data) => {
          const rate = data.acre ? 0.0615 : 0.123;
          return Math.round((data.grossAnnual || 0) * rate);
        }
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon CA et commune',
        defaultValue: 500
      },
      {
        key: 'cfp',
        label: 'Formation (CFP)',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 0.1%',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.001)
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.urssafContributions || 0) + (data.cfe || 0) + (data.cfp || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA × 29% (abattement 71%)',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.29)
      },
      {
        key: 'acre',
        label: 'ACRE 1ère année',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: 'Réduit URSSAF de 50%',
        defaultValue: false
      },
      {
        key: 'versementLiberatoire',
        label: 'Versement libératoire IR',
        type: 'checkbox',
        fieldType: 'manual',
        section: 'options',
        formula: '+1% du CA (IR inclus)',
        defaultValue: false
      }
    ]
  },

  // EI Régime Réel (BNC ou BIC)
  EI_REEL: {
    label: 'EI Réel - Charges',
    icon: '🏪',
    sections: [
      { id: 'charges', label: 'Charges sociales' },
      { id: 'deductions', label: 'Charges déductibles' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      {
        key: 'urssafContributions',
        label: 'Cotisations URSSAF',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'Bénéfice × ~45%',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.45)
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon CA et commune',
        defaultValue: 500
      },
      {
        key: 'cfp',
        label: 'Formation (CFP)',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: 'CA × 0.2%',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.002)
      },
      {
        key: 'realExpenses',
        label: 'Charges réelles déductibles',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: 'Loyer, matériel, sous-traitance...'
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.urssafContributions || 0) + (data.cfe || 0) + (data.cfp || 0) + (data.realExpenses || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA - Charges totales',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'CA - Charges déductibles',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.realExpenses || 0))
      }
    ]
  },

  // LMNP Micro-BIC
  LMNP_MICRO: {
    label: 'LMNP Micro-BIC',
    icon: '🏠',
    sections: [
      { id: 'charges', label: 'Charges' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      {
        key: 'propertyTax',
        label: 'Taxe foncière',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'charges',
        formula: 'Avis d\'imposition'
      },
      {
        key: 'insurance',
        label: 'Assurance PNO',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: '~150€/an',
        defaultValue: 150
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon commune',
        defaultValue: 300
      },
      {
        key: 'socialContributions',
        label: 'Prélèvements sociaux',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: '17.2% si > 23k€, sinon 0',
        calculate: (data) => {
          if ((data.grossAnnual || 0) <= 23000) return 0;
          return Math.round((data.grossAnnual || 0) * 0.172);
        }
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.propertyTax || 0) + (data.insurance || 0) + (data.cfe || 0) + (data.socialContributions || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Loyers - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Loyers × 50% (abattement 50%)',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.50)
      }
    ]
  },

  // LMNP Réel
  LMNP_REEL: {
    label: 'LMNP Réel',
    icon: '🏠',
    sections: [
      { id: 'charges', label: 'Charges fixes' },
      { id: 'deductions', label: 'Déductions réel' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      {
        key: 'propertyTax',
        label: 'Taxe foncière',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'charges',
        formula: 'Avis d\'imposition'
      },
      {
        key: 'insurance',
        label: 'Assurance PNO',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: '~150€/an',
        defaultValue: 150
      },
      {
        key: 'cfe',
        label: 'CFE',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: 'Selon commune',
        defaultValue: 300
      },
      {
        key: 'socialContributions',
        label: 'Prélèvements sociaux',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: '17.2% si > 23k€',
        calculate: (data) => {
          if ((data.grossAnnual || 0) <= 23000) return 0;
          return Math.round((data.grossAnnual || 0) * 0.172);
        }
      },
      {
        key: 'managementFees',
        label: 'Frais de gestion',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'deductions',
        formula: '~8% des loyers',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.08)
      },
      {
        key: 'loanInterest',
        label: 'Intérêts d\'emprunt',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: 'Tableau amortissement'
      },
      {
        key: 'depreciation',
        label: 'Amortissement bien',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: '~2-3%/an de la valeur'
      },
      {
        key: 'furnitureDepreciation',
        label: 'Amortissement meubles',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: '~10-20%/an du mobilier'
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.propertyTax || 0) + (data.insurance || 0) + (data.cfe || 0) +
          (data.socialContributions || 0) + (data.managementFees || 0) +
          (data.loanInterest || 0) + (data.depreciation || 0) + (data.furnitureDepreciation || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Loyers - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Peut être 0 avec amortissements',
        calculate: (data) => Math.max(0, Math.round((data.grossAnnual || 0) - (data.totalCharges || 0)))
      }
    ]
  },

  // Foncier Micro
  FONCIER_MICRO: {
    label: 'Foncier Micro',
    icon: '🏢',
    sections: [
      { id: 'charges', label: 'Charges' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      {
        key: 'propertyTax',
        label: 'Taxe foncière',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'charges',
        formula: 'Avis d\'imposition'
      },
      {
        key: 'insurance',
        label: 'Assurance PNO',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: '~150€/an',
        defaultValue: 150
      },
      {
        key: 'socialContributions',
        label: 'Prélèvements sociaux',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'charges',
        formula: '17.2% du net foncier',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.70 * 0.172)
      },
      {
        key: 'totalCharges',
        label: 'TOTAL CHARGES',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round(
          (data.propertyTax || 0) + (data.insurance || 0) + (data.socialContributions || 0)
        )
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Loyers - Charges',
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalCharges || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Loyers × 70% (abattement 30%)',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.70)
      }
    ]
  },

  // Foncier Réel
  FONCIER_REEL: {
    label: 'Foncier Réel',
    icon: '🏢',
    sections: [
      { id: 'charges', label: 'Charges fixes' },
      { id: 'deductions', label: 'Charges déductibles' },
      { id: 'total', label: '', isTotal: true },
      { id: 'fiscal', label: 'Calcul fiscal' }
    ],
    fields: [
      {
        key: 'propertyTax',
        label: 'Taxe foncière',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'charges',
        formula: 'Avis d\'imposition'
      },
      {
        key: 'insurance',
        label: 'Assurance PNO',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'charges',
        formula: '~150€/an',
        defaultValue: 150
      },
      {
        key: 'condoFees',
        label: 'Charges copropriété',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: 'Part déductible'
      },
      {
        key: 'loanInterest',
        label: 'Intérêts d\'emprunt',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: 'Tableau amortissement'
      },
      {
        key: 'repairs',
        label: 'Travaux déductibles',
        type: 'number',
        fieldType: 'manual',
        suffix: '€',
        section: 'deductions',
        formula: 'Entretien, réparation'
      },
      {
        key: 'managementFees',
        label: 'Frais de gestion',
        type: 'number',
        fieldType: 'estimated',
        suffix: '€',
        section: 'deductions',
        formula: '~8% des loyers',
        calculate: (data) => Math.round((data.grossAnnual || 0) * 0.08)
      },
      {
        key: 'totalDeductions',
        label: 'Total déductions',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'deductions',
        calculate: (data) => Math.round(
          (data.propertyTax || 0) + (data.insurance || 0) + (data.condoFees || 0) +
          (data.loanInterest || 0) + (data.repairs || 0) + (data.managementFees || 0)
        )
      },
      {
        key: 'netFoncier',
        label: 'Revenu foncier net',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'total',
        isTotal: true,
        calculate: (data) => Math.round((data.grossAnnual || 0) - (data.totalDeductions || 0))
      },
      {
        key: 'socialContributions',
        label: 'Prélèvements sociaux',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: '17.2% du net foncier',
        calculate: (data) => Math.round(Math.max(0, (data.netFoncier || 0)) * 0.172)
      },
      {
        key: 'netBeforeTax',
        label: 'Net disponible',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Net foncier - PS',
        calculate: (data) => Math.round((data.netFoncier || 0) - (data.socialContributions || 0))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        suffix: '€',
        section: 'fiscal',
        formula: 'Revenu foncier net',
        calculate: (data) => Math.max(0, Math.round(data.netFoncier || 0))
      }
    ]
  },

  SOCIETE_IR: {
    label: 'Quote-part société IR',
    icon: '🏢',
    fields: [
      {
        key: 'companyName',
        label: 'Nom de la société',
        type: 'text',
        fieldType: 'manual',
        help: 'SCI, SARL de famille, etc.'
      },
      {
        key: 'ownershipPercentage',
        label: 'Pourcentage de détention',
        type: 'number',
        fieldType: 'manual',
        help: 'Votre part dans la société',
        suffix: '%'
      },
      {
        key: 'companyResult',
        label: 'Résultat fiscal de la société',
        type: 'number',
        fieldType: 'manual',
        help: 'Bénéfice ou déficit de la société',
        suffix: '€'
      },
      {
        key: 'yourShare',
        label: 'Votre quote-part',
        type: 'number',
        fieldType: 'auto',
        help: 'Résultat × votre %',
        suffix: '€',
        calculate: (data) => Math.round((data.companyResult || 0) * ((data.ownershipPercentage || 0) / 100))
      },
      {
        key: 'taxableIncome',
        label: 'Revenu imposable',
        type: 'number',
        fieldType: 'auto',
        help: 'Quote-part à déclarer à l\'IR',
        suffix: '€',
        calculate: (data) => Math.round(data.yourShare || 0)
      }
    ]
  }
};

// Field type badge component
const FieldTypeBadge = ({ fieldType }) => {
  const badges = {
    auto: { icon: '🔵', label: 'Auto', title: 'Calculé automatiquement' },
    estimated: { icon: '🟡', label: 'Estimé', title: 'Valeur estimée, à vérifier' },
    manual: { icon: '⚪', label: 'Manuel', title: 'À saisir par l\'utilisateur' }
  };
  const badge = badges[fieldType] || badges.manual;
  return (
    <span className="field-type-badge" title={badge.title}>
      {badge.icon}
    </span>
  );
};

// Help tooltip component
const HelpTooltip = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span
      className="help-tooltip-wrapper"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <span className="help-icon">?</span>
      {isVisible && (
        <div className="help-tooltip">
          {text}
        </div>
      )}
    </span>
  );
};

// Helper to calculate all auto/estimated fields
const calculateAllFields = (data, fields, year) => {
  const result = { ...data };

  // Multiple passes to handle dependencies between fields
  for (let pass = 0; pass < 3; pass++) {
    fields.forEach(field => {
      if (field.calculate) {
        if (field.fieldType === 'auto') {
          result[field.key] = field.calculate(result, year);
        } else if (field.fieldType === 'estimated' && !result[`${field.key}_manual`]) {
          result[field.key] = field.calculate(result, year);
        }
      }
    });
  }

  return result;
};

const IncomeDetailModal = ({
  isOpen,
  onClose,
  incomeType,
  year,
  initialData = {},
  onSave
}) => {
  const [formData, setFormData] = useState({});

  const config = useMemo(() => {
    return INCOME_FIELDS_CONFIG[incomeType] || INCOME_FIELDS_CONFIG.SALARIE;
  }, [incomeType]);

  // Initialize form data with defaults AND calculate all auto fields immediately
  useEffect(() => {
    if (isOpen) {
      const initial = { ...initialData };

      // Apply default values for estimated fields
      config.fields.forEach(field => {
        if (initial[field.key] === undefined && field.defaultValue !== undefined) {
          initial[field.key] = field.defaultValue;
        }
      });

      // Calculate all auto fields immediately
      const calculated = calculateAllFields(initial, config.fields, year);
      setFormData(calculated);
    }
  }, [isOpen, initialData, config, year]);

  // Recalculate when form data changes (user edits)
  const recalculate = (newData) => {
    const calculated = calculateAllFields(newData, config.fields, year);
    setFormData(calculated);
  };

  const handleFieldChange = (key, value, isEstimated = false) => {
    const newData = {
      ...formData,
      [key]: value,
      ...(isEstimated ? { [`${key}_manual`]: true } : {})
    };
    recalculate(newData);
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const renderField = (field) => {
    // Check if field should be shown
    if (field.showIf && !field.showIf(formData)) {
      return null;
    }

    const isAutoCalculated = field.fieldType === 'auto';
    const isReadOnly = field.fieldType === 'readonly';
    const isDisabled = isAutoCalculated || isReadOnly;
    const isEstimated = field.fieldType === 'estimated';

    return (
      <div key={field.key} className={`form-field field-type-${field.fieldType} ${field.isTotal ? 'is-total' : ''}`}>
        <div className="field-row">
          <label>
            <FieldTypeBadge fieldType={field.fieldType} />
            <span className="field-label">{field.label}</span>
          </label>

          {field.type === 'number' && (
            <div className="field-value">
              <div className="input-with-suffix">
                <input
                  type="number"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, parseFloat(e.target.value) || 0, isEstimated)}
                  disabled={isDisabled}
                  className={isDisabled ? 'auto-calculated' : ''}
                />
                {field.suffix && <span className="suffix">{field.suffix}</span>}
              </div>
              {field.formula && <span className="formula">{field.formula}</span>}
            </div>
          )}

          {field.type === 'text' && (
            <div className="field-value">
              <input
                type="text"
                value={formData[field.key] || ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
              />
              {field.formula && <span className="formula">{field.formula}</span>}
            </div>
          )}

          {field.type === 'select' && (
            <div className="field-value">
              <select
                value={formData[field.key] || field.defaultValue || ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                disabled={isDisabled}
              >
                {field.options.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {field.formula && <span className="formula">{field.formula}</span>}
            </div>
          )}

          {field.type === 'checkbox' && (
            <div className="field-value">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData[field.key] || false}
                  onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                />
                <span className="checkbox-text">Oui</span>
              </label>
              {field.formula && <span className="formula">{field.formula}</span>}
            </div>
          )}

          {field.type === 'info' && (
            <div className="field-value">
              <div className="info-text">{field.formula || field.help}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="income-detail-modal-overlay" onClick={onClose}>
      <div className="income-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title">
            <span className="type-icon">{config.icon}</span>
            <h2>{config.label} - Détail {year}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-legend">
          <span className="legend-item"><span className="badge">🔵</span> Auto = Calculé</span>
          <span className="legend-item"><span className="badge">🟡</span> Estimé = À vérifier</span>
          <span className="legend-item"><span className="badge">⚪</span> Manuel = À saisir</span>
        </div>

        <div className="modal-body">
          {config.sections ? (
            // Render with sections
            config.sections.map(section => {
              const sectionFields = config.fields.filter(f => f.section === section.id);
              if (sectionFields.length === 0) return null;

              return (
                <div key={section.id} className={`modal-section ${section.isTotal ? 'total-section' : ''}`}>
                  {section.label && <h3 className="section-title">{section.label}</h3>}
                  <div className="fields-grid">
                    {sectionFields.map(renderField)}
                  </div>
                </div>
              );
            })
          ) : (
            // Render flat (no sections)
            <div className="fields-grid">
              {config.fields.map(renderField)}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="actions">
            <button className="cancel-btn" onClick={onClose}>Annuler</button>
            <button className="save-btn" onClick={handleSave}>Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export { INCOME_FIELDS_CONFIG };
export default IncomeDetailModal;
